import { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Send, Bot, User as UserIcon, MessageSquare } from "lucide-react";
import { API } from "@/lib/api";
import { Link } from "react-router-dom";
import { useI18n, useT } from "@/i18n/I18nContext";

function getOrCreateSession() {
  let id = localStorage.getItem("mva_chat_session");
  if (!id) {
    id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("mva_chat_session", id);
  }
  return id;
}

let _msgCounter = 0;
const mkMsg = (role, content) => ({
  id: `m-${Date.now()}-${++_msgCounter}`,
  role,
  content,
});

export default function Chatbot() {
  const t = useT();
  const { lang } = useI18n();
  const [messages, setMessages] = useState([
    mkMsg("assistant", t("chat.first_message")),
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(getOrCreateSession());
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setMessages((m) => [...m, mkMsg("user", text), mkMsg("assistant", "")]);
    setInput("");
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, language: lang }),
      });
      if (!res.ok || !res.body) {
        throw new Error("Stream failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const data = line.replace(/^data:\s*/, "");
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: copy[copy.length - 1].content + parsed.delta,
                };
                return copy;
              });
            }
          } catch {
            // Partial SSE chunks may not yet be valid JSON — skip silently.
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: t("chat.error") };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <section className="mx-auto w-full max-w-3xl flex-1 px-6 py-12 lg:py-16">
        <span className="label-cap">{t("chat.label")}</span>
        <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
          {t("chat.title")}
        </h1>
        <p className="mt-3 text-[#5C6B68]">
          {t("chat.subtitle")}
        </p>

        <div className="mt-8 card-soft flex flex-col overflow-hidden" data-testid="chat-window">
          <div className="flex items-center gap-3 border-b border-[#E0E4E2] px-6 py-4">
            <div className="grid h-9 w-9 place-items-center rounded-full" style={{ background: "var(--brand)" }}>
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-display text-base font-bold text-[#0B1317]">Avery</div>
              <div className="text-xs text-[#5C6B68]">{t("chat.header.subtitle")}</div>
            </div>
          </div>

          <div className="flex h-[480px] flex-col gap-4 overflow-y-auto px-6 py-6" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${m.role === "user" ? "bg-[#E85D04] text-white" : "bg-[#11322A] text-white"}`}>
                  {m.role === "user" ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                </div>
                <div className={`max-w-[78%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${m.role === "user" ? "bg-[#FDFBF7] text-[#0B1317] border border-[#E0E4E2]" : "bg-[#11322A] text-white"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? <span className="opacity-60">…</span> : null)}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-3 border-t border-[#E0E4E2] px-4 py-4"
            data-testid="chat-form"
          >
            <input
              className="input-field"
              placeholder={t("chat.input_placeholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={streaming}
              data-testid="chat-input"
            />
            <button type="submit" className="btn-primary !py-3" disabled={streaming || !input.trim()} data-testid="chat-send">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm">
          <Link to="/intake" className="inline-flex items-center gap-2 font-bold text-[#11322A] hover:text-[#E85D04]" data-testid="chat-to-form">
            <MessageSquare className="h-4 w-4" /> {t("chat.skip")}
          </Link>
          <a href="tel:+18005551234" className="font-bold text-[#5C6B68] hover:text-[#11322A]">{t("chat.or_call")} 1-800-555-1234</a>
        </div>
      </section>
      <Footer />
    </div>
  );
}
